import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useGoogleLogin } from '@react-oauth/google';
import { authService } from '../../../services/authService';
import { useAuthStore } from '../../../stores/authStore';
import { showToast } from '../../../stores/uiStore';
import { ROUTES } from '../../../config/routes';
import { getOperationErrorMessage } from '../../../config/errorMessages';
import { Button } from '../../../components/ui/button';

interface Props {
  mode: 'login' | 'signup';
  testId?: string;
}

export const GoogleLoginButton: React.FC<Props> = (props) => {
  if (!process.env.REACT_APP_GOOGLE_CLIENT_ID) {
    return null;
  }
  return <GoogleLoginButtonInner {...props} />;
};

const GoogleLoginButtonInner: React.FC<Props> = ({ mode, testId }) => {
  const [isLoading, setIsLoading] = React.useState(false);
  const { setAuth } = useAuthStore();
  const navigate = useNavigate();

  const handleSuccess = async (tokenResponse: any) => {
    const credential = tokenResponse.access_token;
    if (!credential) {
      showToast.error(getOperationErrorMessage('auth'));
      return;
    }
    setIsLoading(true);
    try {
      const res = await authService.googleSignin(credential);
      if (res.success && res.data) {
        setAuth(res.data.user, res.data.tokens);
        showToast.success(mode === 'signup' ? 'アカウントが作成されました' : 'ログインしました');
        navigate(ROUTES.HOME);
      } else {
        showToast.error(res.error?.message || getOperationErrorMessage('auth'));
      }
    } catch {
      showToast.error(getOperationErrorMessage('auth'));
    } finally {
      setIsLoading(false);
    }
  };

  const login = useGoogleLogin({
    onSuccess: handleSuccess,
    onError: () => {
      showToast.error(getOperationErrorMessage('auth'));
    },
    scope: 'openid email profile',
  });

  const label = mode === 'login' ? 'Googleでログイン' : 'Googleで登録';

  return (
    <Button
      type="button"
      variant="outline"
      className="w-full py-3 h-auto rounded-lg border-[#E2E8F0] text-[#0F172A] text-[15px] font-bold hover:bg-[#F8FAFC]"
      onClick={() => login()}
      disabled={isLoading}
      data-testid={testId ?? (mode === 'login' ? 'google-login-button' : 'google-signup-button')}
    >
      <span className="mr-[10px]">
        {isLoading ? (
          <Loader2 className="h-5 w-5 animate-spin" />
        ) : (
          <svg width="18" height="18" viewBox="0 0 48 48">
            <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
            <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
            <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
            <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.31-8.16 2.31-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
          </svg>
        )}
      </span>
      {isLoading ? '処理中...' : label}
    </Button>
  );
};
